import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime } from 'rxjs/operators';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { EmailHistoryService } from '../../_services/email-history/email-history.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { AdminEmailComponent } from '../../shared/admin-ux/admin-email.component';
import {
  EmailHistoryResult, EmailHistorySummary, EmailProcessSummary, EmailSendLogEntry, EmailSendOutcome
} from '../../_models/admin/email-history.models';

/** An outcome as the page shows it: its label, its plain-words meaning and whether it is a problem. */
interface OutcomeInfo {
  value: EmailSendOutcome;
  label: string;
  meaning: string;
  problem: boolean;
  badge: string;
}

/**
 * Email history — every email the system tried to send, and whether each one went.
 *
 * Gated on the EmailHistoryView feature and the admin.email-history switch. Read-only: nothing here sends,
 * resends or changes anything.
 *
 * Top: a summary of the chosen window (KPIs, counts by email, the newest problems), optionally refreshed
 * every minute. Below: the send log itself, filtered and paged by the API.
 *
 * Suppressed means the email's switch is off on System Controls, so it was deliberately not sent. That is
 * normal, so it is shown grey and never counted as a problem.
 */
@Component({
  selector: 'email-history',
  standalone: true,
  imports: [FormsModule, DatePipe, NgClass, MatProgressSpinnerModule, AdminEmailComponent],
  templateUrl: 'email-history.page.html'
})
export class EmailHistoryPage implements OnInit, OnDestroy {

  public static readonly pageSize = 50;
  public static readonly searchDelayMs = 400;
  public static readonly autoRefreshMs = 60_000;
  /** Below this success rate the KPI turns red. */
  public static readonly successThreshold = 0.95;

  public readonly windows = [
    { hours: 24, label: '24 h' },
    { hours: 168, label: '7 days' },
    { hours: 720, label: '30 days' },
  ];

  public readonly outcomes: OutcomeInfo[] = [
    { value: 'Sent', label: 'Sent', meaning: 'SendGrid accepted it.', problem: false,
      badge: 'tw:bg-green-100 tw:text-green-900 tw:border-green-300' },
    { value: 'Suppressed', label: 'Suppressed', meaning: 'Its switch is off on System Controls, so it was deliberately not sent.',
      problem: false, badge: 'tw:bg-gray-100 tw:text-gray-700 tw:border-gray-300' },
    { value: 'Refused', label: 'Refused', meaning: 'A setting it needs (address, template, sender) is missing, so it was not sent.',
      problem: true, badge: 'tw:bg-amber-100 tw:text-amber-900 tw:border-amber-400' },
    { value: 'WordingFailed', label: 'Wording failed', meaning: 'The saved wording could not be filled in, so it was not sent.',
      problem: true, badge: 'tw:bg-amber-100 tw:text-amber-900 tw:border-amber-400' },
    { value: 'Failed', label: 'Failed', meaning: 'SendGrid rejected it or could not be reached.', problem: true,
      badge: 'tw:bg-red-100 tw:text-red-900 tw:border-red-400' },
  ];

  public available: boolean | null = null;

  // ── Summary ──────────────────────────────────────────────────────────────────────────────────────
  public hours: number = 24;
  public summary: EmailHistorySummary | null = null;
  public summaryLoading: boolean = false;
  public summaryError: boolean = false;
  public autoRefresh: boolean = false;
  /** When the summary was last loaded: the "3 h ago" times count from here. */
  public now: number = Date.now();
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private summaryRequest = 0;

  // ── History list ─────────────────────────────────────────────────────────────────────────────────
  public outcome: EmailSendOutcome | '' = '';
  public processKey: string = '';
  public search: string = '';
  /**
   * yyyy-mm-dd from the date inputs, in local time; both days are included. The API's toUtc is exclusive
   * (CreatedUtc < toUtc), so it is sent as midnight after the To day.
   */
  public fromDate: string = '';
  public toDate: string = '';
  public page: number = 0;
  public result: EmailHistoryResult | null = null;
  public listLoading: boolean = false;
  public listError: boolean = false;
  public expandedId: number | null = null;
  private listRequest = 0;
  private readonly searchEdits = new Subject<void>();
  private searchSubscription: Subscription;

  constructor(
    private featureSwitches: FeatureSwitchService,
    private service: EmailHistoryService
  ) {
    this.searchSubscription = this.searchEdits.pipe(debounceTime(EmailHistoryPage.searchDelayMs))
      .subscribe(() => this.filtersChanged());
  }

  ngOnInit(): void {
    // canUseGuard has already required EmailHistoryView on the route.
    this.featureSwitches.isEnabled(FeatureSwitchKeys.emailHistory).subscribe(on => {
      this.available = on;
      if (on) {
        this.loadSummary();
        this.loadList();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.searchSubscription.unsubscribe();
  }

  // ── Summary ──────────────────────────────────────────────────────────────────────────────────────

  public windowChanged(hours: number): void {
    this.hours = hours;
    this.loadSummary();
  }

  public loadSummary(): void {
    this.summaryLoading = true;
    const request = ++this.summaryRequest;
    this.service.getSummary(this.hours).pipe(catchError(() => of(null))).subscribe(summary => {
      if (request !== this.summaryRequest) {
        return;
      }
      this.summaryLoading = false;
      this.summaryError = !summary;
      this.now = Date.now();
      if (summary) {
        this.summary = summary;
      }
    });
  }

  public autoRefreshChanged(on: boolean): void {
    this.autoRefresh = on;
    this.stopTimer();
    if (on) {
      this.refreshTimer = setInterval(() => this.loadSummary(), EmailHistoryPage.autoRefreshMs);
    }
  }

  private stopTimer(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** Emails that went through the gate to be sent: every one logged except those switched off. */
  public get attempted(): number {
    return this.summary ? this.summary.total - this.summary.suppressed : 0;
  }

  /** Refused, wording failed and failed: not sent, and not meant to be. Suppressed is not one. */
  public get problems(): number {
    return this.summary ? this.summary.failed + this.summary.refused + this.summary.wordingFailed : 0;
  }

  public get successLow(): boolean {
    return this.summary?.successRate != null && this.summary.successRate < EmailHistoryPage.successThreshold;
  }

  // ── History list ─────────────────────────────────────────────────────────────────────────────────

  /** Every process key the filter can offer: those in the summary, and whichever is chosen now. */
  public get processKeys(): string[] {
    const keys = new Set((this.summary?.byProcess ?? []).map(p => p.processKey).filter(k => !!k) as string[]);
    if (this.processKey) {
      keys.add(this.processKey);
    }
    return [...keys].sort();
  }

  /** Clicking a row of "By email" shows that email's history. */
  public filterByProcess(row: EmailProcessSummary): void {
    if (!row.processKey) {
      return;
    }
    this.processKey = row.processKey;
    this.filtersChanged();
  }

  public clearFilters(): void {
    this.outcome = '';
    this.processKey = '';
    this.search = '';
    this.fromDate = '';
    this.toDate = '';
    this.filtersChanged();
  }

  /** Called on every keystroke in the search box: the list reloads once typing pauses. */
  public searchEdited(): void {
    this.searchEdits.next();
  }

  /** Any filter change starts again from the first page. */
  public filtersChanged(): void {
    this.page = 0;
    this.expandedId = null;
    this.loadList();
  }

  public get hasPrevious(): boolean {
    return this.page > 0;
  }

  public get hasNext(): boolean {
    return !!this.result && (this.page + 1) * EmailHistoryPage.pageSize < this.result.total;
  }

  public get firstShown(): number {
    return this.result?.items.length ? this.page * EmailHistoryPage.pageSize + 1 : 0;
  }

  public get lastShown(): number {
    return this.result ? this.page * EmailHistoryPage.pageSize + this.result.items.length : 0;
  }

  public previousPage(): void {
    if (this.hasPrevious) {
      this.page--;
      this.expandedId = null;
      this.loadList();
    }
  }

  public nextPage(): void {
    if (this.hasNext) {
      this.page++;
      this.expandedId = null;
      this.loadList();
    }
  }

  public toggle(entry: EmailSendLogEntry): void {
    this.expandedId = this.expandedId === entry.id ? null : entry.id;
  }

  public loadList(): void {
    this.listLoading = true;
    this.listError = false;
    // Only the latest request may update the list: filter changes can return out of order.
    const request = ++this.listRequest;
    this.service.search({
      fromUtc: this.dayStartUtc(this.fromDate, 0),
      toUtc: this.dayStartUtc(this.toDate, 1),
      outcome: this.outcome || null,
      processKey: this.processKey || null,
      search: this.search.trim() || null,
      skip: this.page * EmailHistoryPage.pageSize,
      take: EmailHistoryPage.pageSize
    }).pipe(catchError(() => of(null))).subscribe(result => {
      if (request !== this.listRequest) {
        return;
      }
      this.listLoading = false;
      this.listError = !result;
      this.result = result ? { total: result.total ?? 0, items: result.items ?? [] } : null;
    });
  }

  /** Local midnight of a yyyy-mm-dd date, plus `addDays`, as UTC ISO; null when no date is chosen. */
  private dayStartUtc(date: string, addDays: number): string | null {
    if (!date) {
      return null;
    }
    const [y, m, d] = date.split('-').map(Number);
    return new Date(y, m - 1, d + addDays).toISOString();
  }

  // ── Display helpers ──────────────────────────────────────────────────────────────────────────────

  public info(outcome: EmailSendOutcome): OutcomeInfo {
    return this.outcomes.find(o => o.value === outcome)
      ?? { value: outcome, label: outcome, meaning: '', problem: true, badge: 'tw:bg-red-100 tw:text-red-900 tw:border-red-400' };
  }

  /** The first recipient, then "+n" for the rest. */
  public recipients(entry: EmailSendLogEntry): string {
    const to = entry.to ?? [];
    return to.length ? to[0] + (to.length > 1 ? ` +${to.length - 1}` : '') : '';
  }

  /** "just now", "12 min ago", "3 h ago", "4 days ago". */
  public ago(utc: string | null): string {
    if (!utc) {
      return '';
    }
    const minutes = Math.floor((this.now - new Date(utc).getTime()) / 60_000);
    if (minutes < 1) {
      return 'just now';
    }
    if (minutes < 60) {
      return `${minutes} min ago`;
    }
    const hours = Math.floor(minutes / 60);
    if (hours < 48) {
      return `${hours} h ago`;
    }
    return `${Math.floor(hours / 24)} days ago`;
  }

  public percent(rate: number | null): string {
    return rate == null ? '–' : `${(Math.floor(rate * 1000) / 10).toFixed(1)}%`;
  }
}
